/**
 * DispositionGate — every connected call must have a call-end disposition.
 *
 * Shailendra 2026-10-28: "from now on i want every connected call must have to
 * fill the disposition form even refresh the page or logout so show those
 * forms again ... untill they did not fill the form they will [not] dial next
 * call ... call will pause until all form will clear".
 *
 * The SERVER owns the list (GET /api/ai/voice/pending-dispositions, see
 * manijment-api services/ai-service/pendingDispositions.js): every call on the
 * agent's VOIZ extensions since go-live with talktime >= 1s that has no
 * callreports row yet — one form per call, repeat calls included. Because the
 * list lives on the server, a refresh, logout or another machine cannot lose
 * it; this component only renders it.
 *
 * It does NOT replace or change any existing form. The normal wrap-up form
 * still opens after every call; this popup only appears for a call nobody
 * filled (server `show`: past a 60s grace, never during a live call), and it
 * opens the SAME form the agent already uses (cohort form for cohort agents,
 * the regular one otherwise), saving through the same endpoints.
 *
 * Dialling is blocked server-side while anything is pending (HTTP 409
 * PENDING_DISPOSITION on click-to-call / predictive, dial queue paused with
 * pausedReason 'pending_dispo'). Either signal pops this open immediately.
 *
 * Mounted once in MainLayout, for agents / TLs (role 5 / 13) only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, Typography, Box, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { CallDispositionDialog } from './SoftphoneCall';
import CohortCallDisposition from './CohortCallDisposition';
import { getDecryptDataFromLocal } from '../utils/encrypt';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const POLL_MS = 20 * 1000;
const FORCE_EVENT = 'dispo-gate:force';

const fmtIst = (d) => (d
  ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '');
const fmtDur = (s) => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  return n >= 60 ? `${Math.floor(n / 60)}m ${n % 60}s` : `${n}s`;
};
const countFor = (list, phone) => (list || []).filter((p) => p.phone === phone).length;

export default function DispositionGate() {
  const role = getDecryptDataFromLocal('setRole');
  const gatedRole = role == 5 || role == 13; // eslint-disable-line eqeqeq

  const [data, setData] = useState({ show: false, pending: [], cohort: false });
  const [active, setActive] = useState(null);       // pending call whose form is open
  const [notSaved, setNotSaved] = useState({});     // phone -> true when a submit did not clear it
  // Once the server has refused a dial (or paused the queue) the popup stays
  // up until the list is empty, even inside the 60s grace window.
  const forceRef = useRef(false);
  const activeRef = useRef(null);
  useEffect(() => { activeRef.current = active; }, [active]);

  const load = useCallback(async ({ fresh = false } = {}) => {
    try {
      const params = {};
      if (fresh) params.fresh = 1;
      if (forceRef.current) params.force = 1;
      const r = await axios.get(`${AI_PATH}/api/ai/voice/pending-dispositions`, { params });
      const d = r.data || {};
      if (!d.pending || !d.pending.length) forceRef.current = false;
      setData({ show: !!d.show, pending: d.pending || [], cohort: !!d.cohort });
      return d;
    } catch (e) {
      return null; // transient — next poll retries
    }
  }, []);

  // Poll + refresh on focus; a forced refresh on any dial refusal.
  useEffect(() => {
    if (!gatedRole) return undefined;
    load({ fresh: true });
    const t = setInterval(() => { if (!activeRef.current) load(); }, POLL_MS);
    const onFocus = () => { if (!activeRef.current) load({ fresh: true }); };
    const onForce = () => { forceRef.current = true; if (!activeRef.current) load({ fresh: true }); };
    window.addEventListener('focus', onFocus);
    window.addEventListener(FORCE_EVENT, onForce);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(FORCE_EVENT, onForce);
    };
  }, [gatedRole, load]);

  // Watch every API response for the server's "fill your forms first" signal,
  // so no existing caller has to know this gate exists.
  useEffect(() => {
    if (!gatedRole) return undefined;
    const fire = () => window.dispatchEvent(new Event(FORCE_EVENT));
    const id = axios.interceptors.response.use(
      (res) => {
        const d = res && res.data;
        if (d && (d.pausedReason === 'pending_dispo' || d.reason === 'pending_dispo' || Number(d.pendingDispositions) > 0)) fire();
        return res;
      },
      (err) => {
        if (err && err.response && err.response.data && err.response.data.code === 'PENDING_DISPOSITION') fire();
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [gatedRole]);

  // A form closed — it may have saved, or its save may have been rejected and
  // closed quietly. Ask the server which: the call is only gone once a
  // callreports row exists for it.
  const onFormClosed = useCallback(async () => {
    const was = activeRef.current;
    setActive(null);
    if (!was) return;
    const before = countFor(data.pending, was.phone);
    forceRef.current = true;
    const d = await load({ fresh: true });
    const after = countFor(d && d.pending, was.phone);
    setNotSaved((m) => {
      const next = { ...m };
      if (d && after >= before) next[was.phone] = true; else delete next[was.phone];
      return next;
    });
  }, [data.pending, load]);

  if (!gatedRole) return null;

  const pending = data.pending || [];

  if (active) {
    return data.cohort ? (
      <CohortCallDisposition
        key={active.callId}
        dispo={{ open: true, phone: active.phone, talktime: active.talktime, name: active.name, callId: active.callId, sessionId: null }}
        onClose={onFormClosed}
      />
    ) : (
      <CallDispositionDialog
        key={active.callId}
        dispo={{ open: true, phone: active.phone, talktime: active.talktime, sessionId: null, unregisteredLeadLogsId: null }}
        onClose={onFormClosed}
      />
    );
  }

  if (!data.show || !pending.length) return null;

  return (
    <Dialog
      open
      onClose={() => {}}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="md"
      fullWidth
      // Below the wrap-up forms (1500) so an agent's own form, if still open,
      // stays on top and gets finished first; above lock overlays (1300).
      style={{ zIndex: 1490 }}
    >
      <DialogTitle style={{ paddingBottom: 4 }}>
        Fill the call-end form for {pending.length} connected call{pending.length > 1 ? 's' : ''}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          Dialling is paused until every connected call has its disposition. Each call needs its own form,
          including repeat calls to the same number.
        </Alert>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Number</TableCell>
              <TableCell>Call time</TableCell>
              <TableCell>Talk time</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.map((p) => (
              <TableRow key={p.callId}>
                <TableCell>
                  <Typography style={{ fontSize: 13, fontWeight: 600 }}>{p.name || '—'}</Typography>
                  {p.direction === 'inbound' && <Chip size="small" label="Incoming" style={{ height: 18, fontSize: 10, marginTop: 2 }} />}
                </TableCell>
                <TableCell>{p.phone}</TableCell>
                <TableCell>{fmtIst(p.startedAt)}</TableCell>
                <TableCell>{fmtDur(p.talktime)}</TableCell>
                <TableCell align="right">
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    {notSaved[p.phone] && (
                      <Typography style={{ fontSize: 11, fontWeight: 700, color: '#b71c1c' }}>
                        Not saved — choose another outcome
                      </Typography>
                    )}
                    <Button size="small" variant="contained" color="primary" onClick={() => setActive(p)} style={{ textTransform: 'none' }}>
                      Fill form
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
